import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { toastConfig, swalConfig } from '@/config';
import Swal from 'sweetalert2';
import useRouteStore from '@/hooks/useRouteStore';
import RichTextArea from '@/common/RichTextArea';
import { getSeries, getSerie, getMovies, createCampaign, getContactLists, getCampaign, createCampaignDraft, updateCampaign } from '@/api';
import api from '@/api';

const TYPES = [
  { value: 'announcement', label: 'Anuncio' },
  { value: 'new_series',   label: 'Nueva serie' },
  { value: 'new_episode',  label: 'Nuevo capítulo' },
  { value: 'new_movie',    label: 'Nueva película' },
];

const CampaignForm = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const setRoute = useRouteStore((state) => state.setRoute);

  // Tracks the saved campaign _id (may be set after first draft save)
  const campaignIdRef = useRef(routeId ?? null);

  const [loading, setLoading]       = useState(!!routeId);
  const [sending, setSending]       = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Form fields
  const [name, setName]       = useState('');
  const [from, setFrom]       = useState('MaríaPaz <admin@turkish4love.com>');
  const [replyTo, setReplyTo] = useState('mpaz331@gmail.com');
  const [type, setType]       = useState('announcement');
  const [subject, setSubject] = useState('');
  const [body, setBody]       = useState('');

  // Selectors
  const [seriesList, setSeriesList]     = useState([]);
  const [moviesList, setMoviesList]     = useState([]);
  const [episodes, setEpisodes]         = useState([]);
  const [contactLists, setContactLists] = useState([]);

  const [selectedSerie, setSelectedSerie]           = useState(null);
  const [selectedEpisode, setSelectedEpisode]       = useState(null);
  const [selectedMovie, setSelectedMovie]           = useState(null);
  const [selectedContactList, setSelectedContactList] = useState(null);

  const [loadingSeries, setLoadingSeries]     = useState(false);
  const [loadingMovies, setLoadingMovies]     = useState(false);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);

  // Templates
  const [templates, setTemplates]               = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Preview
  const [previewHtml, setPreviewHtml]       = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Pending auto-select IDs when editing an existing campaign
  const [pendingSerieId, setPendingSerieId]           = useState(null);
  const [pendingMovieId, setPendingMovieId]           = useState(null);
  const [pendingEpisodeNumber, setPendingEpisodeNumber] = useState(null);
  const [pendingTemplateId, setPendingTemplateId]     = useState(null);

  // ── Breadcrumb ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setRoute([
      { name: 'Inicio',    url: '/administracion' },
      { name: 'Correos',   url: '/administracion/correos' },
      { name: 'Campañas',  url: '/administracion/correos/campanas' },
      { name: routeId ? 'Editar campaña' : 'Nueva campaña', url: '' },
    ]);
  }, []);

  // ── Load contact lists ──────────────────────────────────────────────────────
  useEffect(() => {
    getContactLists().then((res) => {
      const data = res.data.result ?? res.data ?? [];
      setContactLists(data.map((l) => ({ value: l._id, label: l.name, total: l.total ?? 0 })));
    });
  }, []);

  // ── Load existing campaign when editing ─────────────────────────────────────
  useEffect(() => {
    if (!routeId) return;
    setLoading(true);
    getCampaign(routeId).then((res) => {
      const c = res.data.result ?? res.data;
      setName(c.name ?? '');
      if (c.from) setFrom(c.from);
      if (c.reply_to) setReplyTo(c.reply_to);
      if (c.subject) setSubject(c.subject);
      setBody(c.html ?? '');

      // Contact list
      if (c.contact_list_id) {
        const cl = c.contact_list_id;
        setSelectedContactList({ value: cl._id ?? cl, label: cl.name ?? cl });
      }

      // Infer type and pending IDs from placeholder_data
      const pd = c.placeholder_data ?? {};
      if (pd.serie_id && pd.episode_number != null) {
        setType('new_episode');
        setPendingSerieId(pd.serie_id);
        setPendingEpisodeNumber(pd.episode_number);
      } else if (pd.serie_id) {
        setType('new_series');
        setPendingSerieId(pd.serie_id);
      } else if (pd.movie_id) {
        setType('new_movie');
        setPendingMovieId(pd.movie_id);
      }

      // Restore template selection
      if (c.template) setPendingTemplateId(c.template);

      // Load preview if body exists
      if (c.html) {
        setLoadingPreview(true);
        api.post('/api/v1/messages/templates/preview', {
          category: c.type ?? 'announcement',
          html: c.html,
          placeholder_data: pd,
        }).then((res) => setPreviewHtml(res.data.html ?? res.data ?? ''))
          .catch(() => setPreviewHtml(''))
          .finally(() => setLoadingPreview(false));
      }
    }).catch(() => {
      toast.error('Error al cargar la campaña', toastConfig);
    }).finally(() => setLoading(false));
  }, [routeId]);

  // ── Load series list ────────────────────────────────────────────────────────
  useEffect(() => {
    if (type === 'new_series' || type === 'new_episode') {
      setLoadingSeries(true);
      getSeries().then((res) => {
        const data = res.data.series ?? res.data;
        setSeriesList(data.map((s) => ({ value: s._id, label: s.titulo_original })));
        setLoadingSeries(false);
      });
    }
  }, [type]);

  // ── Load movies list ────────────────────────────────────────────────────────
  useEffect(() => {
    if (type === 'new_movie') {
      setLoadingMovies(true);
      getMovies().then((res) => {
        const data = res.data.movies ?? res.data;
        setMoviesList(data.map((m) => ({ value: m._id, label: m.titulo_original })));
        setLoadingMovies(false);
      });
    }
  }, [type]);

  // ── Load episodes when serie selected ───────────────────────────────────────
  useEffect(() => {
    if (type === 'new_episode' && selectedSerie) {
      setLoadingEpisodes(true);
      setSelectedEpisode(null);
      setEpisodes([]);
      getSerie(selectedSerie.value).then((res) => {
        const capitulos = res.data.capitulos ?? [];
        setEpisodes(capitulos.map((ep) => ({
          value: ep._id ?? `${ep.temporada}-${ep.numero}`,
          label: `T${ep.temporada} E${ep.numero}`,
          temporada: ep.temporada,
          numero: ep.numero,
        })));
        setLoadingEpisodes(false);
      });
    }
  }, [selectedSerie, type]);

  // ── Auto-select template from pending ID after templates load ──────────────
  useEffect(() => {
    if (pendingTemplateId && templates.length) {
      const raw = [
        { value: null, label: 'Sin plantilla' },
        ...templates.map((t) => ({ value: t._id, label: t.name ?? t.nombre ?? t.title ?? t.category, html: t.html ?? t.body ?? t.content ?? '' })),
      ];
      const match = raw.find((t) => t.value === pendingTemplateId);
      if (match) { setSelectedTemplate(match); setPendingTemplateId(null); }
    }
  }, [templates, pendingTemplateId]);

  // ── Auto-select serie from pending ID after list loads ──────────────────────
  useEffect(() => {
    if (pendingSerieId && seriesList.length) {
      const match = seriesList.find((s) => s.value === pendingSerieId);
      if (match) { setSelectedSerie(match); setPendingSerieId(null); }
    }
  }, [seriesList, pendingSerieId]);

  // ── Auto-select episode from pending number after episodes load ─────────────
  useEffect(() => {
    if (pendingEpisodeNumber != null && episodes.length) {
      const match = episodes.find((e) => e.numero === pendingEpisodeNumber);
      if (match) { setSelectedEpisode(match); setPendingEpisodeNumber(null); }
    }
  }, [episodes, pendingEpisodeNumber]);

  // ── Auto-select movie from pending ID after list loads ──────────────────────
  useEffect(() => {
    if (pendingMovieId && moviesList.length) {
      const match = moviesList.find((m) => m.value === pendingMovieId);
      if (match) { setSelectedMovie(match); setPendingMovieId(null); }
    }
  }, [moviesList, pendingMovieId]);

  // ── Sync selectedContactList total once contactLists loads ──────────────────
  useEffect(() => {
    if (selectedContactList && contactLists.length) {
      const match = contactLists.find((l) => l.value === selectedContactList.value);
      if (match && match.total !== selectedContactList.total) {
        setSelectedContactList(match);
      }
    }
  }, [contactLists]);

  // ── Auto-populate subject from selections ───────────────────────────────────
  useEffect(() => {
    if (type === 'new_series' && selectedSerie) {
      setSubject(`Nueva serie disponible: ${selectedSerie.label}`);
    } else if (type === 'new_episode' && selectedSerie && selectedEpisode) {
      setSubject(`Nuevo capítulo: ${selectedSerie.label} - T${selectedEpisode.temporada} E${selectedEpisode.numero}`);
    } else if (type === 'new_movie' && selectedMovie) {
      setSubject(`Nueva película disponible: ${selectedMovie.label}`);
    }
  }, [selectedSerie, selectedEpisode, selectedMovie, type]);

  // ── Load templates when type changes ────────────────────────────────────────
  useEffect(() => {
    setSelectedTemplate(null);
    setTemplates([]);
    setLoadingTemplates(true);
    api.get(`/api/v1/messages/templates/${type}`).then((res) => {
      const raw = res.data.templates ?? res.data ?? [];
      setTemplates(Array.isArray(raw) ? raw : [raw]);
    }).catch(() => setTemplates([])).finally(() => setLoadingTemplates(false));
  }, [type]);

  // ── Fetch rendered preview ───────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedTemplate?.value) { setPreviewHtml(''); return; }
    setLoadingPreview(true);
    api.post('/api/v1/messages/templates/preview', {
      category: type,
      html: selectedTemplate.html ?? '',
      placeholder_data: {
        ...(selectedSerie   && { serie_id:       selectedSerie.value }),
        ...(selectedMovie   && { movie_id:        selectedMovie.value }),
        ...(selectedEpisode && { episode_number:  selectedEpisode.numero }),
      },
    }).then((res) => setPreviewHtml(res.data.html ?? res.data ?? ''))
      .catch(() => setPreviewHtml(''))
      .finally(() => setLoadingPreview(false));
  }, [selectedTemplate, selectedSerie, selectedEpisode, selectedMovie]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleTypeChange = (newType) => {
    setType(newType);
    setSelectedSerie(null);
    setSelectedEpisode(null);
    setSelectedMovie(null);
    setSubject('');
    setBody('');
    setPreviewHtml('');
  };

  const handleTemplateChange = (option) => {
    setSelectedTemplate(option);
    if (option?.value) setBody(option.html ?? '');
  };

  const buildPlaceholderData = () => ({
    ...(selectedSerie   && { serie_id:      selectedSerie.value }),
    ...(selectedMovie   && { movie_id:       selectedMovie.value }),
    ...(selectedEpisode && { episode_number: selectedEpisode.numero }),
  });

  const handleSaveDraft = async () => {
    if (!name.trim()) { toast.error('El nombre de la campaña es obligatorio', toastConfig); return; }
    setSavingDraft(true);
    try {
      const payload = {
        name,
        from,
        reply_to: replyTo,
        type,
        subject,
        html: body,
        template: selectedTemplate?.value ?? null,
        contact_list_id: selectedContactList?.value ?? null,
        placeholder_data: buildPlaceholderData(),
      };
      if (campaignIdRef.current) {
        await updateCampaign(campaignIdRef.current, payload);
        toast.success('Borrador guardado', toastConfig);
      } else {
        const res = await createCampaignDraft(payload);
        const newId = (res.data.result ?? res.data)._id;
        campaignIdRef.current = newId;
        toast.success('Campaña creada', toastConfig);
        navigate(`/administracion/correos/campanas/${newId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar el borrador', toastConfig);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSend = async () => {
    if (!isValid()) return;
    const contactCount = selectedContactList?.total ?? '?';
    const result = await Swal.fire({
      ...swalConfig.delete,
      title: '¿Enviar campaña?',
      text: `Se enviará "${name}" a ${contactCount} contacto${contactCount !== 1 ? 's' : ''}. Esta acción no se puede deshacer.`,
      confirmButtonText: 'Enviar',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;
    setSending(true);
    try {
      await api.post(`/api/v1/messages/campaigns/${campaignIdRef.current}/send`, {
        type,
        from,
        reply_to: replyTo,
        subject,
        body,
        ...(selectedSerie   && { serieId:   selectedSerie.value }),
        ...(selectedEpisode && { episodeId: selectedEpisode.value }),
        ...(selectedMovie   && { movieId:   selectedMovie.value }),
      });
      toast.success('Campaña enviada correctamente', toastConfig);
      navigate('/administracion/correos/campanas');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al enviar la campaña', toastConfig);
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      await api.post('/api/v1/messages/test', {
        html: selectedTemplate?.html ?? body,
        placeholder_data: buildPlaceholderData(),
      });
      toast.success('Correo de prueba enviado', toastConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al enviar el correo de prueba', toastConfig);
    } finally {
      setSendingTest(false);
    }
  };

  const isValid = () => {
    if (!subject.trim() || !body.trim()) return false;
    if (type === 'new_series' && !selectedSerie) return false;
    if (type === 'new_episode' && (!selectedSerie || !selectedEpisode)) return false;
    if (type === 'new_movie' && !selectedMovie) return false;
    return true;
  };

  if (loading) {
    return (
      <div className='font-montserrat w-full flex flex-col gap-4'>
        <div className='h-8 w-56 animate-pulse bg-gray-100 rounded mb-2' />
        {[...Array(6)].map((_, i) => <div key={i} className='h-10 w-full animate-pulse bg-gray-100 rounded-lg' />)}
      </div>
    );
  }

  return (
    <div className='font-montserrat w-full'>

      {/* Header */}
      <div className='flex items-center justify-between pb-1 mb-5 border-b-2'>
        <h1 className='font-semibold text-xl'>{routeId ? 'Editar campaña' : 'Nueva campaña'}</h1>
        <div className='flex gap-2'>
          {!routeId ? (
            <button
              onClick={handleSaveDraft}
              disabled={!name.trim() || savingDraft}
              className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded-lg hover:bg-red-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              {savingDraft && (
                <svg className='animate-spin h-3.5 w-3.5' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                </svg>
              )}
              Crear campaña
            </button>
          ) : (
            <>
              <button
                onClick={handleSaveDraft}
                disabled={savingDraft}
                className='flex items-center gap-1.5 border border-red-1 text-red-1 hover:bg-red-1 hover:text-white font-semibold text-sm px-4 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                {savingDraft && (
                  <svg className='animate-spin h-3.5 w-3.5' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                  </svg>
                )}
                Guardar borrador
              </button>
              <button
                onClick={handleSend}
                disabled={!isValid() || sending}
                className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded-lg hover:bg-red-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                {sending && (
                  <svg className='animate-spin h-3.5 w-3.5' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                  </svg>
                )}
                Enviar campaña
              </button>
            </>
          )}
        </div>
      </div>

      <div className='flex gap-8 items-start'>

        {/* ── Left: form ── */}
        <div className='flex-1 min-w-0 flex flex-col gap-6'>

          {/* Campaign name */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Nombre</p>
            <input
              id='campaign-name'
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              className='w-full p-1 outline-none border-2 rounded focus:border-red-2 transition-all'
            />
          </div>

          {/* From */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Remitente</p>
            <input
              type='text'
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className='w-full p-1 outline-none border-2 rounded focus:border-red-2 transition-all'
            />
          </div>

          {/* Reply-To */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Responder a</p>
            <input
              type='email'
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              className='w-full p-1 outline-none border-2 rounded focus:border-red-2 transition-all'
            />
          </div>

          {/* Contact list */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Lista de contacto</p>
            <Select
              options={contactLists}
              value={selectedContactList}
              onChange={setSelectedContactList}
              placeholder='Seleccionar lista...'
              isClearable
              className='relative z-40'
            />
          </div>

          {/* Type selector */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Tipo de campaña</p>
            <div className='flex gap-2 flex-wrap'>
              {TYPES.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleTypeChange(value)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                    type === value
                      ? 'bg-red-1 text-white border-red-1'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-red-1 hover:text-red-1'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Series selector */}
          {(type === 'new_series' || type === 'new_episode') && (
            <div>
              <p className='font-semibold text-red-1 mb-2'>Serie</p>
              <Select
                options={seriesList}
                value={selectedSerie}
                onChange={(v) => { setSelectedSerie(v); setSelectedEpisode(null); }}
                isLoading={loadingSeries}
                placeholder='Seleccionar serie...'
                className='relative z-30'
              />
            </div>
          )}

          {/* Episode selector */}
          {type === 'new_episode' && selectedSerie && (
            <div>
              <p className='font-semibold text-red-1 mb-2'>Capítulo</p>
              <Select
                options={episodes}
                value={selectedEpisode}
                onChange={setSelectedEpisode}
                isLoading={loadingEpisodes}
                placeholder='Seleccionar capítulo...'
                className='relative z-20'
              />
            </div>
          )}

          {/* Movie selector */}
          {type === 'new_movie' && (
            <div>
              <p className='font-semibold text-red-1 mb-2'>Película</p>
              <Select
                options={moviesList}
                value={selectedMovie}
                onChange={setSelectedMovie}
                isLoading={loadingMovies}
                placeholder='Seleccionar película...'
                className='relative z-30'
              />
            </div>
          )}

          {/* Email content section */}
          <h2 className='font-semibold text-lg border-b pb-1'>Contenido del correo</h2>

          {/* Subject */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Asunto</p>
            <input
              id='subject'
              type='text'
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className='w-full p-1 outline-none border-2 rounded focus:border-red-2 transition-all'
            />
          </div>

          {/* Template selector */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Plantilla</p>
            <Select
              options={[
                { value: null, label: 'Sin plantilla' },
                ...templates.map((t) => ({ value: t._id, label: t.name ?? t.nombre ?? t.title ?? t.category, html: t.html ?? t.body ?? t.content ?? '' })),
              ]}
              value={selectedTemplate ?? { value: null, label: 'Sin plantilla' }}
              onChange={handleTemplateChange}
              isLoading={loadingTemplates}
              placeholder='Seleccionar plantilla...'
              className='relative z-10'
            />
          </div>

          {/* Body */}
          <div>
            <p className='font-semibold text-red-1 mb-2'>Contenido</p>
            <RichTextArea value={body} onChange={setBody} />
          </div>

        </div>

        {/* ── Right: preview ── */}
        <div className='w-80 xl:w-96 shrink-0 sticky top-0 flex flex-col gap-3'>
          <p className='font-semibold text-red-1 mb-2'>Vista previa</p>
          <div className='rounded-xl border border-gray-200 overflow-hidden shadow-sm bg-white'>

            <div className='bg-gray-100 px-4 py-3 border-b border-gray-200 flex items-center'>
              <span className='flex-1 text-center text-xs text-gray-400 font-medium truncate'>
                {subject || 'Sin asunto'}
              </span>
            </div>

            <div className='px-4 py-3 border-b border-gray-100 flex flex-col gap-1 text-xs text-gray-500'>
              <div className='flex gap-1'>
                <span className='font-semibold text-gray-400 w-14 shrink-0'>De:</span>
                <span className='truncate'>{from}</span>
              </div>
              {replyTo && (
                <div className='flex gap-1'>
                  <span className='font-semibold text-gray-400 w-14 shrink-0 whitespace-nowrap'>Reply-To:</span>
                  <span className='truncate'>{replyTo}</span>
                </div>
              )}
              {selectedContactList && (
                <div className='flex gap-1'>
                  <span className='font-semibold text-gray-400 w-14 shrink-0'>Para:</span>
                  <span className='truncate'>
                    {selectedContactList.label} ({selectedContactList.total ?? 0} contactos)
                  </span>
                </div>
              )}
              <div className='flex gap-1'>
                <span className='font-semibold text-gray-400 w-14 shrink-0'>Asunto:</span>
                <span className='truncate font-semibold text-gray-600'>{subject || '—'}</span>
              </div>
            </div>

            <div className='overflow-y-auto max-h-[500px]'>
              {loadingPreview ? (
                <div className='flex items-center justify-center py-12'>
                  <svg className='animate-spin h-6 w-6 text-red-1' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                  </svg>
                </div>
              ) : previewHtml ? (
                <iframe srcDoc={previewHtml} className='w-full border-none' style={{ height: '500px' }} title='Vista previa del correo' />
              ) : body ? (
                <div className='p-4 text-sm prose prose-sm max-w-none' dangerouslySetInnerHTML={{ __html: body }} />
              ) : (
                <div className='p-4 text-sm text-gray-400 italic text-center py-12'>
                  El contenido del correo aparecerá aquí
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSendTest}
            disabled={sendingTest || (!previewHtml && !body)}
            className='w-full flex items-center justify-center gap-1.5 border border-red-1 text-red-1 hover:bg-red-1 hover:text-white font-semibold text-sm px-4 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {sendingTest ? (
              <svg className='animate-spin h-3.5 w-3.5' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
              </svg>
            ) : (
              <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                <path strokeLinecap='round' strokeLinejoin='round' d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
              </svg>
            )}
            Enviar correo de prueba
          </button>
        </div>

      </div>
    </div>
  );
};

export default CampaignForm;
